import React, { useCallback, useState, useRef } from 'react';
import { Formik, Field } from 'formik';
import * as yup from 'yup';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import { FormattedMessage } from 'react-intl';

import { CREATE_BOOK_WITH_PRODUCT } from '../mutations';

import Button from '../../../components/Button';
import FormInput from '../../../components/FormInput';
import SelectInput from '../../../components/FormInput/SelectInput';

import { GET_PRODUCT } from '../../Product/queries';
import Box from '../../../components/layout/Box';
import CardSection from '../../../containers/CardSection';
import ProductPreviewCard from '../../../components/ProductPreviewCard';

import Text from '../../../components/Text';

const AutoFilledForm = ({ productId }) => {
  const navigate = useNavigate();

  const clear = useCallback(() => {
    navigate('/new?mode=search');
  }, []);

  const { data: product, loading: productLoading } = useQuery(GET_PRODUCT, {
    variables: {
      id: productId,
    },
  });

  const formikRef = useRef();
  const client = useApolloClient();

  const [showErrors, setShowErrors] = useState(false);

  const [showContactError, setContactError] = useState(false);

  const [createBookWithProduct, { loading }] = useMutation(
    CREATE_BOOK_WITH_PRODUCT,
    {
      onCompleted: (result) => {
        client.cache.evict({ fieldName: 'listBooks' });
        navigate(`/new?mode=search&created=${result.createBookWithProduct.id}`);
      },
    },
  );

  const validationSchema = yup.object().shape({
    price: yup
      .number('Priset måste vara ett heltal')
      .required('Obligatoriskt fält')
      .positive('Priset kan inte vara ett negativt belopp')
      .integer('Prister måste vara ett heltal'),
    condition: yup.number().required().positive('Obligatoriskt fält'),
  });

  if (productLoading) {
    return null;
  }

  const createAd = (values) => {
    const { price, condition, comment, contactEmail, contactPhone } = values;

    if (!contactEmail && !contactPhone) {
      setContactError(true);
      return;
    }

    createBookWithProduct({
      variables: {
        input: {
          productId,
          price: parseInt(price, 10),
          comment,
          conditionId: parseInt(condition, 10),
          contactEmail,
          contactPhone,
        },
      },
    });
  };

  const submit = () => {
    setShowErrors(true);
    formikRef.current.handleSubmit();
  };

  const conditionOptions = [
    {
      value: 0,
      title: 'Välj skick',
    },
    {
      value: 1,
      title: 'Ny',
    },
    {
      value: 2,
      title: 'Använd - Nyskick',
    },
    {
      value: 3,
      title: 'Använd - Bra skick',
    },
    {
      value: 4,
      title: 'Använd - Ok skick',
    },
    {
      value: 5,
      title: 'Använd - Sliten',
    },
  ];

  return (
    <Box className="flex-column">
      <CardSection
        className="mt-5 mb-3"
        title={<FormattedMessage defaultMessage="Vald artikel" />}
        after={
          <Button
            paddingHorizontal={0}
            titleColor="errorColor"
            backgroundColor="transparentColor"
            onClick={clear}
          >
            Börja om
          </Button>
        }
      >
        <ProductPreviewCard
          showButtons={false}
          withLargeTitle={false}
          product={product.getProduct}
        />
      </CardSection>

      <CardSection
        title={<FormattedMessage defaultMessage="Fyll i annonsinfo" />}
      >
        <Formik
          innerRef={formikRef}
          validationSchema={validationSchema}
          initialValues={{
            price: '',
            condition: 0,
            comment: '',
            contactPhone: true,
            contactEmail: true,
          }}
          onSubmit={createAd}
        >
          {({ values, errors, handleChange }) => (
            <Form>
              <FormInput
                name="newBookAutofillPrice"
                value={values.price}
                onChange={handleChange('price')}
                label="Pris"
                error={showErrors && errors.price}
              />

              <SelectInput
                name="newBookAutofillCondition"
                value={values.condition}
                onChange={handleChange('condition')}
                label="Bokens skick"
                error={showErrors && errors.condition}
                options={conditionOptions}
              />

              <FormInput
                name="newBookAutofillComment"
                value={values.comment}
                onChange={handleChange('comment')}
                label="Kommentar"
                error={showErrors && errors.comment}
                as="textarea"
                style={{ height: 100, minHeight: 100, maxHeight: 200 }}
              />

              <Box className="flex-column mt-5">
                <Text variant="sectionTitle">Jag vill bli kontaktad via</Text>
                {showContactError && (
                  <Text color="errorColor">Välj minst ett alternativ</Text>
                )}
                <Box className="mt-2">
                  <label className="d-flex flex-row">
                    <Field
                      type="checkbox"
                      name="contactEmail"
                      onChange={handleChange('contactEmail')}
                      style={{ marginRight: 8, width: 20, height: 20 }}
                    />
                    E-post
                  </label>
                </Box>

                <Box>
                  <label className="d-flex flex-row">
                    <Field
                      type="checkbox"
                      onChange={handleChange('contactPhone')}
                      name="contactPhone"
                      style={{ marginRight: 8, width: 20, height: 20 }}
                    />
                    <div>Telefon</div>
                  </label>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>

        <Box className="flex-row-reverse mt-4">
          <Button
            isLoading={loading}
            marginTop="l"
            onClick={submit}
            type="submit"
          >
            Skapa annons
          </Button>
        </Box>
      </CardSection>
    </Box>
  );
};

export default AutoFilledForm;
